/**
 * AcceptBuyOfferModal - Modal for NFT owners to accept a buy offer
 *
 * Flow:
 * 1. Show confirmation with fee breakdown (for marketplace-brokered offers)
 * 2. User confirms and signs transaction via Xaman
 * 3. For marketplace-brokered: creates matching sell offer via /offers/create
 * 4. For P2P: directly accepts buy offer via /offers/accept-p2p
 */

import React, { useState, useEffect } from 'react';
import { Dialog } from '../../ui/components/Dialog';
import { Button } from '../../ui/components/Button';
import { Avatar } from '../../ui/components/Avatar';
import { FeatherX } from '@subframe/core';
import { useAuthStore } from '../../store/authStore';
import { useNotificationStore } from '../../store/notificationStore';
import { apiClient } from '../../api/client';
import { formatPrice } from '../../utils/formatters';
import {
  isSupportedMarketplace,
  calculateSellAmountForBuyOffer,
  getMarketplaceName,
  getMarketplaceFeePercentage,
} from '../../lib/marketplace-constants';
import { WalletSigningModal } from '../transactions/WalletSigningModal';
import { toSigningPayload } from '../../lib/wallet';
import type { SigningPayload } from '../../types/signing';
import type { FormattedOffer } from '../../types';

interface AcceptBuyOfferModalProps {
  isOpen: boolean;
  onClose: () => void;
  offer: FormattedOffer;
  nft: {
    nft_token_id: string;
    name: string;
    thumbnail_url?: string;
  };
  onSuccess?: () => void;
}

type ModalStep = 'confirm' | 'signing' | 'success' | 'error';

export const AcceptBuyOfferModal: React.FC<AcceptBuyOfferModalProps> = ({
  isOpen,
  onClose,
  offer,
  nft,
  onSuccess,
}) => {
  const { user } = useAuthStore();
  const { success: showSuccess } = useNotificationStore();

  const [step, setStep] = useState<ModalStep>('confirm');
  const [error, setError] = useState<string>('');
  // Guards a double-click from firing two offer-create requests during the round-trip
  // (the signing modal only mounts after the request resolves).
  const [confirming, setConfirming] = useState(false);

  // Normalized signing payload (any provider). When set, WalletSigningModal owns the
  // signing lifecycle (Xaman QR / deep link, or WalletConnect approval for joey-wc).
  const [signing, setSigning] = useState<SigningPayload | null>(null);

  // Calculate fee breakdown
  const isP2P = !offer.destination;
  const isMarketplaceBrokered = Boolean(offer.destination && isSupportedMarketplace(offer.destination));
  const isUnsupportedMarketplace = Boolean(offer.destination && !isSupportedMarketplace(offer.destination));

  const marketplaceName = getMarketplaceName(offer.destination);
  const feePercentage = getMarketplaceFeePercentage(offer.destination);

  // For marketplace-brokered offers, seller pays the fee
  // sellAmount = buyAmount * (1 - fee%)
  const sellerReceives = isMarketplaceBrokered
    ? calculateSellAmountForBuyOffer(offer.amount, offer.destination)
    : offer.amount;
  const feeAmount = offer.amount - sellerReceives;

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep('confirm');
      setError('');
      setSigning(null);
    }
  }, [isOpen]);

  // WalletSigningModal resolved the signing in our favor.
  const handleSignedSuccess = () => {
    setSigning(null);
    setStep('success');
    showSuccess(`Offer accepted! NFT sold for ${formatPrice(sellerReceives / 1_000_000)}`);
    setTimeout(() => {
      onSuccess?.();
      onClose();
    }, 1500);
  };

  const handleConfirm = async () => {
    if (!user?.xrpl_address || confirming) return;

    setError('');
    setConfirming(true);

    try {
      let response;

      if (isMarketplaceBrokered) {
        // For marketplace-brokered buy offers, create a matching sell offer
        // Seller pays the fee: sellAmount = buyAmount * (1 - fee%)
        response = await apiClient.post<any>('/offers/create', {
          nftTokenId: nft.nft_token_id,
          offerType: 'sell',
          amount: sellerReceives,
          userAddress: user.xrpl_address,
          destination: offer.destination, // Same broker as the buy offer
        });
      } else {
        // For P2P buy offers, directly accept the buy offer
        response = await apiClient.post<any>('/offers/accept-p2p', {
          nftTokenId: nft.nft_token_id,
          offerIndex: offer.offerIndex,
          userAddress: user.xrpl_address,
          offerType: 'buy',
        });
      }

      // Normalize across providers: Xaman returns `payload`, joey-wc returns `signingIntent`.
      const normalized = response.data?.signingIntent ?? response.data?.payload;
      if (response.success && normalized) {
        setSigning(toSigningPayload(normalized));
      } else {
        setError(response.error || 'Failed to create transaction');
        setStep('error');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create transaction');
      setStep('error');
    } finally {
      setConfirming(false);
    }
  };

  const handleClose = () => {
    if (step === 'success') {
      onSuccess?.();
    }
    onClose();
  };

  const getTitle = () => {
    switch (step) {
      case 'confirm':
        return 'Accept Buy Offer';
      case 'signing':
        return 'Sign Transaction';
      case 'success':
        return 'Sale Complete!';
      case 'error':
        return 'Error';
      default:
        return 'Accept Offer';
    }
  };

  // Format address for display
  const formatAddress = (address: string): string => {
    return `${address.slice(0, 8)}...${address.slice(-6)}`;
  };

  return (
    <>
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <Dialog.Content className="w-full max-w-[480px]">
        <div className="flex w-full flex-col items-start gap-6 p-6">
          {/* Header */}
          <div className="flex w-full items-start justify-between">
            <span className="text-heading-2 font-heading-2 text-default-font">
              {getTitle()}
            </span>
            <button
              type="button"
              onClick={handleClose}
              className="flex h-8 w-8 items-center justify-center rounded hover:bg-neutral-100"
            >
              <FeatherX className="h-5 w-5 text-subtext-color" />
            </button>
          </div>

          {/* Content */}
          {step === 'confirm' && (
            <div className="flex flex-col gap-4 w-full">
              {/* NFT Preview */}
              <div className="flex items-center gap-4 p-4 bg-neutral-50 rounded-lg">
                <Avatar size="large" image={nft.thumbnail_url}>
                  {nft.name?.charAt(0) || '?'}
                </Avatar>
                <div className="flex flex-col">
                  <span className="text-body-bold text-default-font">
                    {nft.name || 'Unnamed NFT'}
                  </span>
                  <span className="text-caption text-subtext-color">
                    {formatAddress(nft.nft_token_id)}
                  </span>
                </div>
              </div>

              {/* Unsupported marketplace warning */}
              {isUnsupportedMarketplace && (
                <div className="bg-error-50 border border-error-200 rounded-md p-3">
                  <p className="text-body text-error-600">
                    This offer uses an unsupported marketplace and cannot be accepted.
                  </p>
                </div>
              )}

              {/* Fee breakdown */}
              {!isUnsupportedMarketplace && (
                <div className="flex flex-col gap-2">
                  <div className="flex justify-between text-body">
                    <span className="text-subtext-color">From</span>
                    <span className="text-default-font font-mono text-sm">
                      {formatAddress(offer.creator)}
                    </span>
                  </div>
                  <div className="flex justify-between text-body">
                    <span className="text-subtext-color">Offer Amount</span>
                    <span className="text-default-font">
                      {formatPrice(offer.amount / 1_000_000)}
                    </span>
                  </div>
                  {isP2P ? (
                    <div className="flex justify-between text-body">
                      <span className="text-subtext-color">Type</span>
                      <span className="text-success-600">P2P (no fee)</span>
                    </div>
                  ) : (
                    <>
                      <div className="flex justify-between text-body">
                        <span className="text-subtext-color">Marketplace</span>
                        <span className="text-default-font">{marketplaceName}</span>
                      </div>
                      <div className="flex justify-between text-body">
                        <span className="text-subtext-color">Fee ({feePercentage}%)</span>
                        <span className="text-default-font">
                          -{formatPrice(feeAmount / 1_000_000)}
                        </span>
                      </div>
                    </>
                  )}
                  <div className="flex justify-between text-body-bold border-t border-neutral-border pt-2 mt-1">
                    <span className="text-default-font">You Receive</span>
                    <span className="text-success-600">
                      {formatPrice(sellerReceives / 1_000_000)}
                    </span>
                  </div>
                </div>
              )}

              {/* Explanation */}
              {!isUnsupportedMarketplace && (
                <div className="bg-neutral-50 rounded-md p-3">
                  <p className="text-caption text-subtext-color">
                    {isP2P ? (
                      <>
                        <span className="font-semibold text-default-font">Direct P2P Sale:</span>{' '}
                        You'll sign a transaction to accept this buy offer directly.
                        No marketplace fees apply.
                      </>
                    ) : (
                      <>
                        <span className="font-semibold text-default-font">Marketplace Sale:</span>{' '}
                        You'll sign a sell offer that the {marketplaceName} broker will match with
                        the buyer's offer. The {feePercentage}% marketplace fee is deducted from
                        your proceeds.
                      </>
                    )}
                  </p>
                </div>
              )}
            </div>
          )}

          {step === 'success' && (
            <div className="flex flex-col items-center gap-4 py-8 w-full">
              <div className="w-16 h-16 rounded-full bg-success-100 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-success-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <p className="text-body-bold text-default-font text-center">Sale Complete!</p>
              <p className="text-body text-subtext-color text-center">
                You received {formatPrice(sellerReceives / 1_000_000)} for {nft.name}
              </p>
            </div>
          )}

          {step === 'error' && (
            <div className="flex flex-col items-center gap-4 py-4 w-full">
              <div className="w-16 h-16 rounded-full bg-error-100 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-error-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <p className="text-body text-error-600 text-center">
                {error || 'Something went wrong'}
              </p>
            </div>
          )}

          {/* Footer */}
          <div className="flex w-full items-center justify-end gap-3">
            {step === 'confirm' && (
              <>
                <Button variant="neutral-secondary" onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  variant="brand-primary"
                  onClick={handleConfirm}
                  disabled={isUnsupportedMarketplace || confirming}
                  loading={confirming}
                >
                  Confirm Sale
                </Button>
              </>
            )}

            {(step === 'success' || step === 'error') && (
              <Button variant="neutral-secondary" onClick={handleClose}>
                Close
              </Button>
            )}
          </div>
        </div>
      </Dialog.Content>
    </Dialog>

    {signing && (
      <WalletSigningModal
        open={true}
        onClose={() => setSigning(null)}
        title="Sign Transaction"
        payload={signing}
        onSuccess={handleSignedSuccess}
        onRejected={() => {
          setSigning(null);
          setError('Transaction was rejected');
          setStep('error');
        }}
        onExpired={() => {
          setSigning(null);
          setError('Transaction request expired');
          setStep('error');
        }}
      />
    )}
    </>
  );
};
